import { useMutation, useQuery } from '@tanstack/react-query';
import { aiReviewApi, reportApi } from '@/shared/api/report';

/** --- 리포트 --- */

const useReportSummary = (param: API.EvalReviewSummary.Req) =>
    useQuery({
        queryKey: ['report', 'summary', param],
        queryFn: () => reportApi.getReportEvalReviewSummary(param),
    });

const useReportEvalDetailList = (param: API.ReportEvaluationDetailList.Req, enabled = true) =>
    useQuery({
        queryKey: ['report', 'report-list', param],
        queryFn: () => reportApi.getReportEvalDetailList(param),
        enabled: enabled,
    });

const useReportEvalDetailMdul = (param: API.ReportEvaluationDetailMdul.Req, enabled = true) =>
    useQuery({
        queryKey: ['report', 'report-detail-mdul', param],
        queryFn: () => reportApi.getReportEvalDetailMdul(param),
        enabled: enabled,
    });

const useReportEvalDetailStnt = (
    param: API.ReportEvaluationDetailStnt.Req,
    enabled: boolean = true
) =>
    useQuery({
        queryKey: ['list', 'data', param],
        queryFn: () => reportApi.getReportEvalDetailStnt(param),
        enabled: enabled,
    });

const useStntEvalResutInfo = (param: API.StntEvlResultInfo.Req, enabled: boolean) =>
    useQuery({
        queryKey: ['list', 'questions', param],
        queryFn: () => reportApi.getStntEvalResutInfo(param),
        enabled: enabled,
    });

const useReportEvalModiScore = () =>
    useMutation({
        mutationKey: ['stnt-srch', 'modify'],
        mutationFn: (param: API.ReportEvaluationModifyScore.Req) =>
            reportApi.postReportEvalModiScore(param),
    });

const useReportEvalFdbMod = () =>
    useMutation({
        mutationKey: ['report', 'feedback'],
        mutationFn: (param: API.ReportEvaluationFdbModify.Req) =>
            reportApi.postReportEvalFdbMod(param),
    });

/** --- 학생 조회 --- */

const useStntSrchEvalResultDetail = (param: API.StntEvalResultDetail.Req) =>
    useQuery({
        queryKey: ['stnt-srch', 'report-eval-reuslt-detail', param],
        queryFn: () => reportApi.getReportEvalResultDetail(param),
        enabled: !!param.evlId && !!param.stntId,
    });

/** --- 공유 --- */
const useReportEvalOpen = () =>
    useMutation({
        mutationKey: ['report-share-open'],
        mutationFn: (param: API.ReportEvaluationOpen.Req) => reportApi.postReportEvalOpen(param),
    });

const useReportEvalApplScore = () =>
    useMutation({
        mutationKey: ['report-re-share-appl-score'],
        mutationFn: (param: API.ReportEvaluationApplScore.Req) =>
            reportApi.postReportEvalApplScore(param),
    });

/** --- AI 총평 --- */

const useTchReportEvalGeneralReviewAiEvlWord = (
    param: API.TchReportGeneralReviewAiEvlWord.Req,
    enabled = true
) =>
    useQuery({
        queryKey: ['report', 'ai-review-meta', param],
        queryFn: () => aiReviewApi.getTchReportEvalGeneralReviewAiEvlWord(param),
        enabled: enabled,
    });

const useTchDsbdOftensentsList = (param: API.TchDsbdOftensentsList.Req, enabled = true) =>
    useQuery({
        queryKey: ['oftensent', 'list', param],
        queryFn: () => aiReviewApi.getTchDsbdOftensentsList(param),
        enabled: enabled,
    });

const useReportEvalReviewInfo = (param: API.EvalReviewInfo.Req, enabled = true) =>
    useQuery({
        queryKey: ['report', 'ai-review-info', param],
        queryFn: () => aiReviewApi.getReportEvalReviewInfo(param),
        enabled: enabled,
    });

const useOverallReview = () =>
    useMutation({
        mutationKey: ['report', 'ai-review-overall'],
        mutationFn: (param: API.OverallReview.Req) => aiReviewApi.postOverallReview(param),
    });

const useReportEvalReviewSave = () =>
    useMutation({
        mutationKey: ['report', 'ai-review-save'],
        mutationFn: (param: API.EvalReviewSave.Req) => aiReviewApi.postReportEvalReviewSave(param),
    });

const useTchDsbdOftensentsSave = () =>
    useMutation({
        mutationKey: ['oftensent', 'save'],
        mutationFn: (param: API.TchDsbdOftensentsSave.Req) =>
            aiReviewApi.postTchDsbdOftensentsSave(param),
    });

const useTchDsbdOftensentsMod = () =>
    useMutation({
        mutationKey: ['oftensent', 'modify'],
        mutationFn: (param: API.TchDsbdOftensentsMod.Req) =>
            aiReviewApi.postTchDsbdOftensentsMod(param),
    });

const useTchDsbdOftensentsDel = () =>
    useMutation({
        mutationKey: ['oftensent', 'delete'],
        mutationFn: (param: API.TchDsbdOftensentsDel.Req) =>
            aiReviewApi.postTchDsbdOftensentsDel(param),
    });

export const reportQueries = {
    stnts: {
        getReportSummary: useReportSummary,
        getReportEvalDetailList: useReportEvalDetailList,
        getReportEvalDetailMdul: useReportEvalDetailMdul,
        getReportEvalDetailStnt: useReportEvalDetailStnt,
        postReportEvalModiScore: useReportEvalModiScore,
    },
    stnt: {
        getStntSrchEvalResultDetail: useStntSrchEvalResultDetail,
    },
    share: {
        getStntEvalResutInfo: useStntEvalResutInfo,
        postReportEvalOpen: useReportEvalOpen,
        postReportEvalApplScore: useReportEvalApplScore,
        postReportEvalFdbMod: useReportEvalFdbMod,
    },
    review: {
        getTchReportEvalGeneralReviewAiEvlWord: useTchReportEvalGeneralReviewAiEvlWord,
        getReportEvalReviewInfo: useReportEvalReviewInfo,
        getTchDsbdOftensentsList: useTchDsbdOftensentsList,
        postOverallReview: useOverallReview,
        postReportEvalReviewSave: useReportEvalReviewSave,
        postTchDsbdOftensentsSave: useTchDsbdOftensentsSave,
        postTchDsbdOftensentsMod: useTchDsbdOftensentsMod,
        postTchDsbdOftensentsDel: useTchDsbdOftensentsDel,
    },
};
